<template>
  <div class="lang" role="group" :aria-label="t('navigation.language')">
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      class="lang__option"
      :class="{ 'is-current': currentLocale === option.value }"
      :aria-pressed="currentLocale === option.value ? 'true' : 'false'"
      :title="option.label"
      @click="setLocale(option.value)"
    >
      <span aria-hidden="true">{{ option.short }}</span>
      <span class="visually-hidden">{{ option.label }}</span>
    </button>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'LanguageSwitcher',
  setup() {
    const { locale, t } = useI18n();

    /*
     * The mounted hook used to force `en` and write it to localStorage whenever
     * nothing was stored — which overrode the app's own locale resolution on
     * every first visit. i18n.js already resolves the locale (stored choice,
     * then browser language, then Ukrainian); this component only reflects it.
     */
    const currentLocale = ref(locale.value);

    watch(locale, (value) => {
      currentLocale.value = value;
    });

    const setLocale = (value) => {
      if (value === currentLocale.value) return;
      currentLocale.value = value;
      locale.value = value;
      localStorage.setItem('locale', value);
      document.documentElement.setAttribute('lang', value);
    };

    return {
      t,
      currentLocale,
      setLocale,
      options: [
        { value: 'uk', short: 'UA', label: 'Українська' },
        { value: 'en', short: 'EN', label: 'English' },
      ],
    };
  },
};
</script>

<style scoped>
/*
 * Was a native <select> at min-width 8.5rem showing the full word
 * "Українська" — a 136px browser widget with its own chrome, its own font
 * rendering and its own height, sitting in a 34px designed button row. It was
 * the largest element in the header after the wordmark, for the control
 * visitors touch least.
 *
 * Two locales do not need a menu. This is a two-state toggle at the size the
 * choice deserves; the full language names stay in the accessible name.
 */
.lang {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 2px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.04);
}

.lang__option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 var(--space-2);
  border: none;
  border-radius: 3px;
  background: transparent;
  color: var(--text-faint);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: color var(--duration) var(--ease),
              background-color var(--duration) var(--ease);
}

.lang__option:hover {
  color: var(--text-muted);
}

.lang__option.is-current {
  background: rgba(255, 255, 255, 0.10);
  color: var(--text);
}
</style>
